import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard, CalendarDays, Hotel as HotelIcon, Image as ImageIcon,
  Star, IndianRupee, BarChart3, MessageSquare, LogOut, BookOpen,
  ChevronRight, Sparkles, Menu, X, BedDouble,
} from 'lucide-react';

const NAV = [
  { id: 'home', label: 'Home', icon: LayoutDashboard },
  { id: 'reservations', label: 'Reservations', icon: BookOpen },
  { id: 'calendar', label: 'Calendar', icon: CalendarDays },
  { id: 'rooms', label: 'Rooms', icon: BedDouble },
  { id: 'rates', label: 'Rates & Boost', icon: Sparkles },
  { id: 'property', label: 'Property', icon: HotelIcon },
  { id: 'photos', label: 'Photos', icon: ImageIcon },
  { id: 'reviews', label: 'Reviews', icon: Star },
  { id: 'finance', label: 'Finance', icon: IndianRupee },
  { id: 'insights', label: 'Insights', icon: BarChart3 },
  { id: 'inbox', label: 'Inbox', icon: MessageSquare },
];

export const AdminShell = ({ activeId, onNav, unreadInbox, headerStats, children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Close drawer when nav changes (mobile)
  useEffect(() => { setMobileOpen(false); }, [activeId]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="hotel-admin-shell">
      {/* ============ MOBILE TOP STRIP ============ */}
      <div className="lg:hidden sticky top-[65px] z-40 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 rounded-full hover:bg-gray-100"
          aria-label="Open menu"
          data-testid="hotel-admin-mobile-menu-btn"
        >
          <Menu className="h-5 w-5" />
        </button>
        <span className="font-black text-black truncate max-w-[180px]">{user?.hotel_name || 'Hotel Admin'}</span>
        <button onClick={handleLogout} className="p-2 rounded-full hover:bg-gray-100" aria-label="Logout">
          <LogOut className="h-5 w-5" />
        </button>
      </div>

      <div className="flex">
        {/* ============ SIDEBAR ============ */}
        <aside
          className={`fixed lg:sticky top-[65px] left-0 z-50 lg:z-30 h-[calc(100vh-65px)] w-72 bg-white border-r border-gray-200 flex flex-col transition-transform duration-200 ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
          data-testid="hotel-admin-sidebar"
        >
          {/* property card */}
          <div className="p-5 border-b border-gray-100 flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF0059] to-[#CC0047] text-white flex items-center justify-center font-black text-lg flex-shrink-0">
              {(user?.hotel_name || 'H').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-black text-black tracking-tight truncate" data-testid="sidebar-hotel-name">{user?.hotel_name || 'My Hotel'}</p>
              <p className="text-xs text-gray-500 truncate">{user?.city_area || user?.hotel_address || '—'}</p>
              <div className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full bg-[#FFE5EE] text-[10px] font-bold uppercase tracking-wider text-[#CC0047]">
                Hotel Admin
              </div>
            </div>
            <button
              onClick={() => setMobileOpen(false)}
              className="lg:hidden p-1 rounded-full hover:bg-gray-100"
              aria-label="Close menu"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>

          {/* nav */}
          <nav className="flex-1 overflow-y-auto py-2" data-testid="sidebar-nav">
            {NAV.map((item) => {
              const Icon = item.icon;
              const isActive = activeId === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => onNav(item.id)}
                  className={`w-full flex items-center justify-between gap-3 px-5 py-2.5 text-sm font-bold transition-colors ${isActive ? 'bg-[#FFE5EE] text-[#CC0047] border-r-2 border-[#FF0059]' : 'text-gray-700 hover:bg-gray-50'}`}
                  data-testid={`sidebar-nav-${item.id}`}
                >
                  <span className="flex items-center gap-3">
                    <Icon className="h-4 w-4" />
                    {item.label}
                  </span>
                  {item.id === 'inbox' && unreadInbox > 0 && (
                    <span className="inline-flex items-center justify-center text-[10px] font-black bg-[#FF0059] text-white rounded-full min-w-[20px] h-5 px-1.5" data-testid="sidebar-inbox-unread">
                      {unreadInbox}
                    </span>
                  )}
                  {isActive && <ChevronRight className="h-4 w-4" />}
                </button>
              );
            })}
          </nav>

          {/* footer actions */}
          <div className="p-4 border-t border-gray-100 space-y-1">
            <Link to="/" className="flex items-center gap-3 px-3 py-2 text-sm font-bold text-gray-700 rounded-lg hover:bg-gray-50" data-testid="sidebar-view-public">
              <HotelIcon className="h-4 w-4" /> View public page
            </Link>
            <Link
              to={user?._id ? `/hotels/${user._id}` : '/'}
              className="flex items-center gap-3 px-3 py-2 text-sm font-bold text-[#FF0059] rounded-lg hover:bg-[#FFE5EE]"
              data-testid="sidebar-see-listing"
            >
              <Sparkles className="h-4 w-4" /> See your Listing
            </Link>
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2 text-sm font-bold text-gray-700 rounded-lg hover:bg-gray-50" data-testid="sidebar-logout-btn">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </aside>

        {/* dim background when drawer open on mobile */}
        {mobileOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black/40 z-40"
            onClick={() => setMobileOpen(false)}
          />
        )}

        {/* ============ MAIN PANEL ============ */}
        <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-10 py-6 lg:py-10">
          {/* sticky top stats strip on desktop */}
          {headerStats && headerStats.length > 0 && (
            <div className="hidden lg:flex items-center gap-3 mb-7 flex-wrap" data-testid="hotel-admin-header-stats">
              {headerStats.map((s) => (
                <div key={s.label} className="bg-white border border-gray-200 rounded-2xl px-4 py-2.5 flex items-center gap-3 min-w-[160px]">
                  <s.icon className="h-4 w-4 text-[#FF0059]" />
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{s.label}</p>
                    <p className="text-lg font-black text-black leading-tight">{s.value}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
};
